import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, Save, X } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const FAQManagement = () => {
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [formData, setFormData] = useState({
    question: '',
    answer: '',
    category: 'general',
    order: 0
  });

  const categories = [
    { value: 'general', label: 'Général' },
    { value: 'machines', label: 'Machines' },
    { value: 'sav', label: 'SAV' },
    { value: 'formation', label: 'Formation' }
  ];

  useEffect(() => {
    fetchFAQs();
  }, []);

  const fetchFAQs = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/faqs`);
      setFaqs(response.data); // Correction : setFaqs au lieu de setFAQs
      setLoading(false);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('adminToken');
      if (editing) {
        await axios.put(`${API_URL}/api/faqs/${editing}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        await axios.post(`${API_URL}/api/faqs`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      fetchFAQs();
      resetForm();
    } catch (error) {
      console.error('Error saving FAQ:', error);
      alert('Erreur lors de la sauvegarde');
    }
  };

  const handleEdit = (faq) => {
    setEditing(faq.id);
    setFormData({
      question: faq.question,
      answer: faq.answer,
      category: faq.category,
      order: faq.order
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette FAQ ?')) return;
    
    try {
      const token = localStorage.getItem('adminToken');
      await axios.delete(`${API_URL}/api/faqs/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchFAQs();
    } catch (error) {
      console.error('Error deleting FAQ:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setEditing(null);
    setFormData({
      question: '',
      answer: '',
      category: 'general',
      order: 0
    });
  };

  if (loading) {
    return <div className="text-center py-12">Chargement...</div>;
  }

  return (
    <div className="space-y-8">
      {/* Form */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-2xl font-bold mb-6" style={{ color: '#5C3A21' }}>
          {editing ? 'Modifier une FAQ' : 'Ajouter une FAQ'}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold mb-2">Question *</label>
            <input
              type="text"
              value={formData.question}
              onChange={(e) => setFormData({ ...formData, question: e.target.value })}
              required
              className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
              placeholder="Quelle est la question ?"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold mb-2">Réponse *</label>
            <textarea
              value={formData.answer}
              onChange={(e) => setFormData({ ...formData, answer: e.target.value })}
              required
              rows={4}
              className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
              placeholder="La réponse détaillée..."
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold mb-2">Catégorie *</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
              >
                {categories.map(cat => (
                  <option key={cat.value} value={cat.value}>{cat.label}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-semibold mb-2">Ordre d'affichage</label>
              <input
                type="number"
                value={formData.order}
                onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) })}
                className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-amber-400 outline-none"
              />
            </div>
          </div>

          <div className="flex space-x-4">
            <button
              type="submit"
              className="px-6 py-2 rounded-lg text-white font-semibold flex items-center space-x-2"
              style={{ backgroundColor: '#5C3A21' }}
            >
              <Save className="w-4 h-4" />
              <span>{editing ? 'Modifier' : 'Ajouter'}</span>
            </button>
            {editing && (
              <button
                type="button"
                onClick={resetForm}
                className="px-6 py-2 rounded-lg bg-gray-200 text-gray-700 font-semibold flex items-center space-x-2"
              >
                <X className="w-4 h-4" />
                <span>Annuler</span>
              </button>
            )}
          </div>
        </form>
      </div>

      {/* FAQ List */}
      <div className="bg-white p-6 rounded-lg shadow-md">
        <h2 className="text-2xl font-bold mb-6" style={{ color: '#5C3A21' }}>
          Liste des FAQs ({faqs.length})
        </h2>

        {faqs.length === 0 ? (
          <p className="text-gray-600 text-center py-8">Aucune FAQ pour le moment</p>
        ) : (
          <div className="space-y-4">
            {faqs.map((faq) => (
              <div key={faq.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex justify-between items-start mb-2">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3 mb-2">
                      <span className="px-3 py-1 rounded-full text-xs font-semibold" style={{ backgroundColor: '#F5EBDD', color: '#5C3A21' }}>
                        {categories.find(c => c.value === faq.category)?.label || faq.category}
                      </span>
                      <span className="text-xs text-gray-500">Ordre: {faq.order}</span>
                    </div>
                    <h3 className="font-bold text-lg mb-2" style={{ color: '#5C3A21' }}>
                      {faq.question}
                    </h3>
                    <p className="text-gray-700">{faq.answer}</p>
                  </div>
                  <div className="flex space-x-2 ml-4">
                    <button
                      onClick={() => handleEdit(faq)}
                      className="p-2 rounded-lg bg-blue-100 text-blue-600 hover:bg-blue-200"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(faq.id)}
                      className="p-2 rounded-lg bg-red-100 text-red-600 hover:bg-red-200"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FAQManagement;
